// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define terms.
 *
 * The audience is a professional, not a developer, so the definitions explain
 * what a thing MEANS FOR THEM rather than what it is in the file format. The
 * two that matter most are the two most likely to be misunderstood in a way
 * that costs somebody something: "resolved" (which does not mean removed) and
 * "revision-save ID" (which cannot be put in order, however much it looks like
 * it should).
 */

export const GLOSSARY: Record<string, { title: string; body: string }> = {
  'tracked-change': {
    title: 'Tracked change',
    body: 'An edit Word recorded rather than applied — an insertion or a deletion, stored with the name the file has against it. Deleted text is kept in full. Until the changes are accepted or rejected and the file is saved, every one of them travels with the document.',
  },
  resolved: {
    title: 'Resolved comment',
    body: 'A comment somebody marked as dealt with. Word stops showing it in the margin, which is the whole problem: it is still in the file, with its author, its date and its full text, and anyone you send the document to can read it.',
  },
  'hidden-text': {
    title: 'Hidden text',
    body: 'Text formatted so Word neither displays nor prints it. It is ordinary text in the file — turning on one setting reveals it, and any program other than Word reads it without being asked.',
  },
  crop: {
    title: 'Crop',
    body: 'Cropping a picture in Word hides the edges; it does not remove them. The whole original file stays inside the document, so everything outside the crop can be recovered by whoever receives it. Word can delete the hidden parts, but only if you ask it to.',
  },
  'contrast-ratio': {
    title: 'Contrast ratio',
    body: 'How different a text colour is from what is behind it, from 1 (identical, so invisible) to 21 (black on white). This is measured rather than guessed at, so pale grey on white is caught and white text on a black cell — which is perfectly readable — is not reported.',
  },
  'editing-session': {
    title: 'Revision-save ID',
    body: 'An identifier Word stamps into the file once per editing session. It carries no name and no date, and the identifiers cannot be put in order — Word allocates them at random and stores them sorted. What they do reveal is linkage: two documents that share them were built from the same source file.',
  },
  'custom-property': {
    title: 'Custom property',
    body: 'A field added by something other than Word — usually a document-management system. Matter numbers, client names, classification labels and retention codes live here. None of them appear anywhere on the page.',
  },
  'content-control': {
    title: 'Content control',
    body: 'A structured placeholder in a document — a date picker, a dropdown, a field bound to a record in another system. Text inside one is easy for a simple extraction tool to walk straight past.',
  },
  'document-variable': {
    title: 'Document variable',
    body: 'A named value stored by a macro or a mail merge. It never appears on the page, survives every visible edit, and can only be removed with a macro or by copying the content into a new file.',
  },
  orphan: {
    title: 'Orphaned link',
    body: 'A web address or file path still stored in the document after the text that linked to it was deleted. Nothing in Word will ever show it to you again, and it is still there.',
  },
};

export function mountGlossary(): () => void {
  let tip: HTMLDivElement | null = null;

  const close = () => {
    tip?.remove();
    tip = null;
  };

  const onPointer = (event: Event) => {
    const target = (event.target as HTMLElement | null)?.closest?.('.glossary-link') as HTMLElement | null;
    if (!target) {
      close();
      return;
    }
    event.preventDefault();
    const entry = GLOSSARY[target.dataset.term ?? ''];
    if (!entry) return;
    close();
    tip = document.createElement('div');
    tip.className = 'glossary-tip';
    tip.setAttribute('role', 'tooltip');
    const strong = document.createElement('strong');
    strong.textContent = entry.title;
    tip.append(strong, document.createTextNode(entry.body));
    document.body.append(tip);

    const rect = target.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 24);
    tip.style.width = `${width}px`;
    tip.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`;
    const below = rect.bottom + 8;
    tip.style.top =
      below + tip.offsetHeight > window.innerHeight ? `${Math.max(12, rect.top - tip.offsetHeight - 8)}px` : `${below}px`;
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  };

  document.addEventListener('pointerdown', onPointer);
  document.addEventListener('keydown', onKey);
  window.addEventListener('resize', close);

  return () => {
    document.removeEventListener('pointerdown', onPointer);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', close);
    close();
  };
}
